// The sum tool (Σ, Marko 2026-11-03): the combinations of a total over a number of cells, which
// the player marks green (in / this one) or red (not in / not this one). Its total and size come
// from the selected cage or arrow, or start at 3 cells and 15 elsewhere, and the player can set
// any of them. Arithmetic only: totals, sizes and placed digits, nothing the solver found.
import type { ClueMarks, HelperMark, PuzzleDoc } from '@zinc/shared';
import { cellAt, type Geometry } from './geometry';

/** A cage or an arrow the sum tool can start from. */
export interface Clue {
  /** Its type and cells, which the marks are saved under. */
  key: string;
  type: 'cage' | 'arrow';
  /** Every cell of the clue (the cage; the arrow's circle and shaft), tinted while it's open. */
  cells: number[];
  /** The cells whose digits make a combination: the cage, or the arrow's shaft. */
  part: number[];
  /** The cage's total. */
  total: number | null;
  /** The arrow's circle: one cell, or an ellipse of two or three read as a number, in reading
   * order. */
  circle: number[];
  /** Digits in `part` may repeat: the cage says so, or the shaft's cells don't all see each other. */
  repeats: boolean;
}

/** What the panel works with. A null total is an arrow's circle not filled in yet ("?"). */
export interface SumValues {
  total: number | null;
  cells: number;
  repeats: boolean;
}

/** Where the panel starts away from a cage or arrow (Marko, 2026-11-03). */
export const FREE_START: SumValues = { total: 15, cells: 3, repeats: false };

export interface Combination {
  /** The symbols joined by commas, smallest first: the key its mark is saved under. */
  key: string;
  digits: string[];
}

/** More than this many combinations and the panel says so instead (only a 16×16 with repeats gets
 * there: a 9×9's longest list is 910, nine cells of 45 with repeats). */
export const MAX_COMBINATIONS = 2000;
/** Lines of combinations before the list scrolls inside the panel. */
export const MAX_LINES = 5;

/** Rules that give cells values other than their digits: sums would mislead. */
const VALUE_RULES = ['negative-digits', 'liars', 'contract-killer'];

/** Numeric values of the puzzle's symbols: digits as themselves, letters from 1. */
export function symbolValues(digits: readonly string[]): Map<string, number> {
  const numeric = digits.every((d) => /^\d+$/.test(d));
  return new Map(digits.map((d, i) => [d, numeric ? Number(d) : i + 1]));
}

const rcs = (v: unknown): [number, number][] =>
  Array.isArray(v)
    ? v.filter((x): x is [number, number] => Array.isArray(x) && x.length === 2)
    : [];

function cellsOf(g: Geometry, list: [number, number][]): number[] | null {
  const out = list.map(([r, c]) => cellAt(g, r, c));
  return out.every((i) => i >= 0) ? out : null;
}

const keyOf = (type: string, list: [number, number][]) =>
  `${type}:${list.map(([r, c]) => `${r},${c}`).join('|')}`;

const allSee = (g: Geometry, cells: number[]) =>
  cells.every((a, i) => cells.every((b, j) => i === j || g.peers[a]!.includes(b)));

/** Every cage with a total and every arrow (circle or ellipse of up to three cells). */
export function allClues(doc: PuzzleDoc, g: Geometry): Clue[] {
  if (doc.constraints.some((e) => VALUE_RULES.includes(e.type))) return [];
  const numeric = doc.grid.digits.every((d) => /^\d$/.test(d));
  const out: Clue[] = [];
  for (const e of doc.constraints) {
    if (e.negative === true) continue;
    if (e.type === 'killer' && typeof e.sum === 'number' && e.counting !== true) {
      const list = rcs(e.cells);
      const cells = cellsOf(g, list);
      if (!cells || cells.length === 0) continue;
      out.push({
        key: keyOf('killer', list),
        type: 'cage',
        cells,
        part: cells,
        total: e.sum,
        circle: [],
        repeats: e.repeats === true,
      });
    } else if (e.type === 'arrow') {
      const circleList = rcs(e.circle);
      const shaftList = rcs(e.cells);
      const circle = cellsOf(g, circleList);
      const shaft = cellsOf(g, shaftList);
      if (!circle || circle.length === 0 || circle.length > 3 || !shaft || shaft.length === 0) {
        continue;
      }
      // An ellipse reads as a number: only with the digits 1–9.
      if (circle.length > 1 && !numeric) continue;
      out.push({
        key: keyOf('arrow', [...circleList, ...shaftList]),
        type: 'arrow',
        cells: [...circle, ...shaft],
        part: shaft,
        total: null,
        circle: [...circle].sort((a, b) => a - b),
        repeats: !allSee(g, shaft),
      });
    }
  }
  return out;
}

/** The arrow's circle as a number, once all of it is filled in. */
function circleNumber(clue: Clue, values: readonly (string | null)[], sv: Map<string, number>) {
  const ds = clue.circle.map((i) => values[i]);
  if (ds.some((d) => !d)) return null;
  return ds.length === 1 ? (sv.get(ds[0]!) ?? null) : Number(ds.join(''));
}

/** The clue's own total, size and repeats. */
export function clueValues(
  clue: Clue,
  values: readonly (string | null)[],
  digits: readonly string[],
): SumValues {
  return {
    total: clue.type === 'cage' ? clue.total : circleNumber(clue, values, symbolValues(digits)),
    cells: clue.part.length,
    repeats: clue.repeats,
  };
}

/** The smallest and largest sums of `cells` digits, or null when there aren't enough digits. */
export function reach(
  cells: number,
  repeats: boolean,
  digits: readonly string[],
): [number, number] | null {
  const vs = [...symbolValues(digits).values()].sort((a, b) => a - b);
  if (cells < 1) return null;
  if (repeats) return [cells * vs[0]!, cells * vs[vs.length - 1]!];
  if (cells > vs.length) return null;
  const sum = (xs: number[]) => xs.reduce((a, b) => a + b, 0);
  return [sum(vs.slice(0, cells)), sum(vs.slice(vs.length - cells))];
}

/** The totals an arrow's empty (or partly filled) circle can still be, for this many cells. */
export function circleCan(
  clue: Clue,
  values: readonly (string | null)[],
  g: Geometry,
  digits: readonly string[],
  sum: SumValues,
): number[] {
  const sv = symbolValues(digits);
  const range = reach(sum.cells, sum.repeats, digits);
  if (!range) return [];
  const options = clue.circle.map((i) => (values[i] ? [values[i]] : [...digits]));
  const out = new Set<number>();
  const go = (k: number, acc: string[]) => {
    if (k === options.length) {
      const n = acc.length === 1 ? sv.get(acc[0]!)! : Number(acc.join(''));
      if (n >= range[0] && n <= range[1]) out.add(n);
      return;
    }
    for (const d of options[k]!) {
      // Cells of the circle that see each other can't repeat a digit.
      if (acc.some((x, j) => x === d && g.peers[clue.circle[k]!]!.includes(clue.circle[j]!))) {
        continue;
      }
      go(k + 1, [...acc, d]);
    }
  };
  go(0, []);
  return [...out].sort((a, b) => a - b);
}

/** 3, 4, …, 9 → "3 to 9"; 12–19, 21, 23, 24 → "12 to 19, 21, 23 or 24". */
export function listText(vs: readonly number[]): string {
  const runs: [number, number][] = [];
  for (const v of vs) {
    const r = runs[runs.length - 1];
    if (r && v === r[1] + 1) r[1] = v;
    else runs.push([v, v]);
  }
  const parts = runs.flatMap(([a, b]) =>
    b - a >= 2 ? [`${a} to ${b}`] : a === b ? [`${a}`] : [`${a}`, `${b}`],
  );
  return parts.length <= 1
    ? (parts[0] ?? '')
    : `${parts.slice(0, -1).join(', ')} or ${parts[parts.length - 1]}`;
}

/** Whether `have` holds every symbol of `need`, as many times as `need` lists it. */
function holds(have: string[], need: readonly string[]): boolean {
  const left = [...have];
  for (const d of need) {
    const i = left.indexOf(d);
    if (i < 0) return false;
    left.splice(i, 1);
  }
  return true;
}

/**
 * The combinations of `sum` without the symbols in `without` and with every symbol of `need`;
 * `over` when there are more than `cap` (the list then stops there).
 */
export function combinations(
  sum: SumValues,
  digits: readonly string[],
  without: readonly string[] = [],
  need: readonly string[] = [],
  cap = MAX_COMBINATIONS,
): { list: Combination[]; over: boolean } {
  const list: Combination[] = [];
  if (sum.total === null || sum.cells < 1) return { list, over: false };
  const sv = symbolValues(digits);
  const all = [...digits].sort((a, b) => sv.get(a)! - sv.get(b)!);
  const use = all.filter((d) => !without.includes(d));
  const top = sv.get(all[all.length - 1]!)!;
  const total = sum.total;
  let over = false;
  const go = (from: number, left: number, s: number, acc: string[]) => {
    if (over) return;
    if (left === 0) {
      if (s !== total || !holds(acc, need)) return;
      if (list.length === cap) over = true;
      else list.push({ key: acc.join(','), digits: acc });
      return;
    }
    for (let i = from; i < use.length; i++) {
      const v = sv.get(use[i]!)!;
      if (s + v * left > total) break;
      if (s + v + top * (left - 1) < total) continue;
      go(sum.repeats ? i : i + 1, left - 1, s + v, [...acc, use[i]!]);
    }
  };
  go(0, sum.cells, 0, []);
  return { list, over };
}

export type SumView =
  /** An arrow whose circle isn't filled in: the totals it can still be. */
  | { kind: 'pick'; can: number[] }
  /** No combination at all: what that many cells can add up to (null: not enough digits). */
  | { kind: 'none'; reach: [number, number] | null }
  /** Combinations exist, but the placed digits or the marks rule out every one. */
  | { kind: 'ruled'; byPlaced: boolean }
  /** More than MAX_COMBINATIONS. */
  | { kind: 'over' }
  | {
      kind: 'rows';
      rows: (Combination & { mark: HelperMark | undefined })[];
      /** Digits in every combination left that isn't red, and in none. */
      always: Set<string>;
      never: Set<string>;
    };

/**
 * What the panel shows. Combinations without a placed digit, with a red digit or without a green
 * one are left out (they come back when what ruled them out goes); a tap on a combination only
 * colours it (Marko, 2026-11-03).
 */
export function sumView(
  sum: SumValues,
  digits: readonly string[],
  placed: readonly string[],
  marks: ClueMarks | undefined,
  can: () => number[],
): SumView {
  if (sum.total === null) return { kind: 'pick', can: can() };
  const digitMarks = marks?.digits ?? {};
  const green = Object.keys(digitMarks).filter((d) => digitMarks[d] === 1);
  const red = Object.keys(digitMarks).filter((d) => digitMarks[d] === 2);
  const { list, over } = combinations(sum, digits, red, [...placed, ...green]);
  if (over) return { kind: 'over' };
  if (list.length === 0) {
    if (combinations(sum, digits, [], [], 1).list.length === 0) {
      return { kind: 'none', reach: reach(sum.cells, sum.repeats, digits) };
    }
    return { kind: 'ruled', byPlaced: combinations(sum, digits, [], placed, 1).list.length === 0 };
  }
  const rowMarks = marks?.rows ?? {};
  const rows = list.map((c) => ({ ...c, mark: rowMarks[c.key] }));
  const open = rows.filter((r) => r.mark !== 2);
  const always = new Set(digits.filter((d) => open.every((r) => r.digits.includes(d))));
  const never = new Set(digits.filter((d) => !open.some((r) => r.digits.includes(d))));
  if (open.length === 0) {
    always.clear();
    never.clear();
  }
  return { kind: 'rows', rows, always, never };
}

/**
 * Tiles across for `count` combinations of `chars` characters in `width` pixels: no more than four
 * lines need, and no more than fit. Past MAX_LINES, as many as fit, and the list scrolls.
 */
export function tileLayout(
  width: number,
  chars: number,
  count: number,
): { cols: number; scroll: boolean } {
  const tile = chars * 9.6 + Math.max(0, chars - 1) * 3 + 18;
  const fit = Math.max(1, Math.floor((width + 6) / (tile + 6)));
  const cols = Math.min(fit, Math.max(1, Math.ceil(count / 4)));
  return Math.ceil(count / cols) > MAX_LINES
    ? { cols: fit, scroll: true }
    : { cols, scroll: false };
}

/** The next mark on a tap: none → green → red → none. */
export function nextMark(m: HelperMark | undefined): HelperMark | undefined {
  return m === undefined ? 1 : m === 1 ? 2 : undefined;
}

/** The marks after a tap on a combination. */
export function tapRow(marks: ClueMarks | undefined, key: string): ClueMarks {
  const rows = { ...(marks?.rows ?? {}) };
  const next = nextMark(rows[key]);
  if (next === undefined) delete rows[key];
  else rows[key] = next;
  return { rows, digits: { ...(marks?.digits ?? {}) } };
}

/** The marks after a tap on a digit. */
export function tapDigit(marks: ClueMarks | undefined, digit: string): ClueMarks {
  const digits = { ...(marks?.digits ?? {}) };
  const next = nextMark(digits[digit]);
  if (next === undefined) delete digits[digit];
  else digits[digit] = next;
  return { rows: { ...(marks?.rows ?? {}) }, digits };
}

/** The key a clue's marks are saved under: its cells, with the total and size they belong to. */
export function marksKey(clue: Clue, sum: SumValues): string {
  return `${clue.key}|${sum.total ?? '?'}|${sum.cells}|${sum.repeats ? 1 : 0}`;
}
